import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import { ReactComponent as RefreshIcon } from '@/assets/icons/refresh-icon.svg';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { FacebookPostComment } from '@/types/resources';
import { api } from '@/utils/api';

import Comment from './Comment';

const Box = styled.div<{ $posted: boolean }>`
  margin-bottom: 16px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;

  ${({ $posted }) =>
    $posted &&
    css`
      position: relative;
      &::before {
        position: absolute;
        z-index: 5;
        left: 0;
        top: 10;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.5);
        content: ' ';
      }
    `}
`;

const BoxHeader = styled.div`
  display: grid;
  grid-template-columns: 22px 1fr 1fr 1fr;
  grid-gap: 24px;
  padding: 16px 24px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
  position: relative;
`;

const BoxContent = styled.div`
  display: grid;
  grid-template-columns: 22px 1fr 1fr 1fr;
  grid-gap: 24px;
  padding: 16px 24px;
`;

const spinAnimation = keyframes`
  from {
      transform:rotate(0deg);
  }
  to {
      transform:rotate(-360deg);
  } 
`;

const GenerateRepliesButton = styled.button<{ $isLoading: boolean }>`
  height: 31px;
  display: inline-flex;
  align-items: center;
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  right: 24px;
  margin: auto 0;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 0 12px;
  gap: 8px;
  transition: 0.2s ease-in-out border-color;
  color: ${({ theme }) => theme.colors.black};

  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }

  svg {
    width: 15px;
  }

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      svg {
        animation-name: ${spinAnimation};
        animation-duration: 2000ms;
        animation-iteration-count: infinite;
        animation-timing-function: linear;
      }
    `}
`;

interface Props {
  comment: FacebookPostComment;
  no: number;
}

const CommentAndReplies: FC<Props> = ({ comment, no }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const queryClient = useQueryClient();

  const toasts = useToasts();
  const alerts = useAlerts();

  const { id } = useParams();

  const onRegenerate = async () => {
    try {
      if (comment.reply1 || comment.reply2) {
        const confirmed = await alerts.confirm('Are you sure?');

        if (!confirmed) {
          return;
        }

        alerts.close();
      }

      setIsGenerating(true);

      await api.patch(
        `/v1/editorial/facebook-post-comments/${comment.id}/regenerate`,
      );

      await queryClient.invalidateQueries([
        'editorial_distribution_schedules',
        {
          id,
        },
      ]);
    } catch (_) {
      toasts.create('error', 'Generation failed!');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box $posted={!!comment.repliedVersion}>
      <BoxHeader>
        <Text $type="smallHeadline">Nr</Text>
        <Text $type="smallHeadline">Comment</Text>
        <Text $type="smallHeadline">Reply 1</Text>
        <Text $type="smallHeadline">Reply 2</Text>
        <GenerateRepliesButton
          $isLoading={isGenerating}
          onClick={onRegenerate}
          disabled={isGenerating}
        >
          <RefreshIcon />
          <Text $type="smallHeadline">Regenerate</Text>
        </GenerateRepliesButton>
      </BoxHeader>
      <BoxContent>
        <Text $type="smallBold">{no}.</Text>
        <Text $type="smallBold">{comment.comment}</Text>
        <Comment
          version={1}
          message={comment.reply1}
          isPosted={comment.repliedVersion === 1}
          commentId={comment.id}
        />
        <Comment
          version={2}
          message={comment.reply2}
          isPosted={comment.repliedVersion === 2}
          commentId={comment.id}
        />
      </BoxContent>
    </Box>
  );
};

export default CommentAndReplies;
